import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _classCallCheck from "@babel/runtime/helpers/classCallCheck";
import _createClass from "@babel/runtime/helpers/createClass";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
function _createForOfIteratorHelper(r, e) { var t = "undefined" != typeof Symbol && r[Symbol.iterator] || r["@@iterator"]; if (!t) { if (Array.isArray(r) || (t = _unsupportedIterableToArray(r)) || e && r && "number" == typeof r.length) { t && (r = t); var _n = 0, F = function F() {}; return { s: F, n: function n() { return _n >= r.length ? { done: !0 } : { done: !1, value: r[_n++] }; }, e: function e(r) { throw r; }, f: F }; } throw new TypeError("Invalid attempt to iterate non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); } var o, a = !0, u = !1; return { s: function s() { t = t.call(r); }, n: function n() { var r = t.next(); return a = r.done, r; }, e: function e(r) { u = !0, o = r; }, f: function f() { try { a || null == t.return || t.return(); } finally { if (u) throw o; } } }; }
function _unsupportedIterableToArray(r, a) { if (r) { if ("string" == typeof r) return _arrayLikeToArray(r, a); var t = {}.toString.call(r).slice(8, -1); return "Object" === t && r.constructor && (t = r.constructor.name), "Map" === t || "Set" === t ? Array.from(r) : "Arguments" === t || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(t) ? _arrayLikeToArray(r, a) : void 0; } }
function _arrayLikeToArray(r, a) { (null == a || a > r.length) && (a = r.length); for (var e = 0, n = Array(a); e < a; e++) n[e] = r[e]; return n; }
/**
 * An envelope representing a deferred promise. It is resolved or rejected after a remote client (the {@link receiverId})
 * handles a command sent by the sender ({@link senderId}). The promise is resolved or rejected based on the {@link RPCResponse}.
 */

/**
 * Maintains all asynchronous, deferred state for delegated RPC method calls.
 *
 * I.e., if JS context 1 invokes a command which is handled by JS context 2, {@link ResponseHandler} is
 * responsible for ensuring the result passed back by JS context 2 is routed to the correct promise being
 * awaited by JS context 1. Streamlines handling of method calls over JS contexts.
 */
export var ResponseHandler = /*#__PURE__*/function () {
  function ResponseHandler() {
    _classCallCheck(this, ResponseHandler);
    /**
     * A mapping from unique command invocations to deferred promises waiting
     * for their result. Used for match-making the result of a delegated RPC call
     * back to its callee. TODO - hook up to methods in CNS-18261.
     */
    _defineProperty(this, "responses", new Map());
  }
  return _createClass(ResponseHandler, [{
    key: "getOrCreate",
    value:
    /**
     * Creates a deferred promise handler for a given command invocation.
     * @param id unique ID for the command invocation
     * @param senderId the callee
     * @param receiverId the receiver which implements a handler for the command
     * @returns a deferrable which is resolved upon a response being yielded by the receiver
     */
    function getOrCreate(id, senderId, receiverId) {
      // if we already have a deferred promise waiting for this specific invocation, we do not need another one.
      var existing = this.get(id);
      if (existing) {
        return existing.promise;
      }
      var deferredResolve;
      var deferredReject;
      var promise = new Promise(function (resolve, reject) {
        deferredResolve = resolve;
        deferredReject = reject;
      });
      var result = {
        senderId: senderId,
        receiverId: receiverId,
        promise: promise,
        // @ts-expect-error - established in above callback
        resolve: deferredResolve,
        // @ts-expect-error - established in above callback
        reject: deferredReject
      };
      this.responses.set(id, result);
      return promise;
    }

    /**
     * Yields the current deferrable state for a command invocation.
     * @param id unique ID for the command invocation.
     * @returns deferrable state for a command invocation.
     */
  }, {
    key: "get",
    value: function get(id) {
      return this.responses.get(id);
    }

    /**
     * Resolves a promise reference based upon the ID of an RPC response.
     *
     * @description If the RPC response is successful, will resolve the promise with the expected return shape.
     * @description If the RPC response is unsuccessful, will reject the promise with an error.
     *
     * @param _response response yielded by the receiver of the original message.
     */
  }, {
    key: "resolveWithResponse",
    value: function resolveWithResponse(response) {
      var id = response.id;
      var deferred = this.responses.get(id);

      // no one waiting - exit
      if (!deferred) {
        return;
      }

      // drop the deferrable from storage now
      this.responses.delete(id);

      // yield correct outcome
      if (response.error) {
        deferred.reject(new Error(response.error.message));
      } else {
        deferred.resolve(response.result);
      }
    }

    /**
     * Cancels a specific promise by resolving it to undefined to avoid unhandled rejections
     * in fire-and-forget scenarios.
     *
     * @description In JavaScript if you do the following:
     * ```typescript
     * const promise = () => new Promise((_, reject) => reject(new Error('oh boy')));
     * promise();
     * ```
     * Even though you are not explicitly calling `await` on the result of the promise, it will
     * reject and result in an unhandled rejection. Specifically when working with commands, we
     * often want to send them in a fire-and-forget manner. When a 'cancellation' takes place,
     * we therefore DO NOT want an unhandled rejection to result. Hence, we treat all command
     * results similar to distributed system communications - as always nullable.
     *
     * Cancellation events to consider: component unmounts, route transitions.
     *
     * For more, see:
     * - {@link https://developer.mozilla.org/en-US/docs/Web/API/Window/unhandledrejection_event}
     * - {@link https://developer.mozilla.org/en-US/docs/Web/JavaScript/Guide/Using_promises#promise_rejection_events}
     * - {@link https://typescript-eslint.io/rules/no-floating-promises/}
     *
     * @param id unique ID for the command invocation to cancel
     */
  }, {
    key: "cancel",
    value: function cancel(id) {
      var deferred = this.responses.get(id);
      if (!deferred) {
        return;
      }

      // clear out in-memory reference
      this.responses.delete(id);

      // resolve to undefined to avoid unhandled promise rejections
      deferred.resolve(undefined);
    }

    /**
     * Clears response handlers for a specific client to prevent memory leaks during client teardown.
     *
     * @param clientId The client ID to clear responses for (as sender or receiver)
     */
  }, {
    key: "teardown",
    value: function teardown(clientId) {
      var toDelete = [];
      var _iterator = _createForOfIteratorHelper(this.responses),
        _step;
      try {
        for (_iterator.s(); !(_step = _iterator.n()).done;) {
          var _step$value = _slicedToArray(_step.value, 2),
            _id = _step$value[0],
            deferred = _step$value[1];
          if (deferred.senderId === clientId || deferred.receiverId === clientId) {
            // Resolve to undefined to avoid unhandled promise rejections
            deferred.resolve(undefined);
            toDelete.push(_id);
          }
        }
      } catch (err) {
        _iterator.e(err);
      } finally {
        _iterator.f();
      }
      for (var _i = 0, _toDelete = toDelete; _i < _toDelete.length; _i++) {
        var id = _toDelete[_i];
        this.responses.delete(id);
      }
    }

    /**
     * Destroy all relevant state.
     */
  }, {
    key: "clear",
    value: function clear() {
      // Resolve all pending promises to avoid unhandled rejections
      var _iterator2 = _createForOfIteratorHelper(this.responses.values()),
        _step2;
      try {
        for (_iterator2.s(); !(_step2 = _iterator2.n()).done;) {
          var deferred = _step2.value;
          deferred.resolve(undefined);
        }
      } catch (err) {
        _iterator2.e(err);
      } finally {
        _iterator2.f();
      }
      this.responses.clear();
    }
  }]);
}();